import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/feature/EmptyState';
import FilterPanel, { type FilterState } from './components/FilterPanel';
import Pagination from './components/Pagination';
import { filterMovies, sortMovies, type SortKey } from '@/utils/movie';
import { GENRES, REGIONS, YEAR_RANGES } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 12;

const TYPE_INFO: Record<string, { title: string; desc: string }> = {
  movie: { title: '电影', desc: '精选亚洲各国院线大片与经典电影，全部配备中文字幕，随时点播。' },
  tv: { title: '电视剧', desc: '汇聚中国、日本、韩国、泰国等地热播剧集，中文字幕同步跟播。' },
  hot: { title: '热门影视', desc: '依据播放热度与评分综合推荐，这里有你一定不能错过的作品。' },
};

const SORT_OPTIONS: { key: SortKey; label: string; icon: string }[] = [
  { key: 'latest', label: '最新', icon: 'ri-time-line' },
  { key: 'hot', label: '最热', icon: 'ri-fire-line' },
  { key: 'rating', label: '评分', icon: 'ri-star-line' },
];

const EMPTY_FILTER: FilterState = { regions: [], yearLabel: null, genres: [] };

export default function CategoryPage() {
  const { type = 'movie', filter } = useParams();
  const typeInfo = TYPE_INFO[type] ?? TYPE_INFO.movie;

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTER);
  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  usePageMeta(
    `${typeInfo.title} - 亚洲中文字幕免费在线观看`,
    `${typeInfo.desc} 亚洲中文字幕在线观看为你整理${typeInfo.title}分类下的高清影视资源。`,
  );

  useEffect(() => {
    const next: FilterState = { regions: [], yearLabel: null, genres: [] };
    if (filter) {
      if (REGIONS.includes(filter)) next.regions = [filter];
      else if (GENRES.includes(filter)) next.genres = [filter];
    }
    setFilters(next);
    setSort('latest');
    setPage(1);
  }, [type, filter]);

  const results = useMemo(() => {
    const yearRange = filters.yearLabel
      ? YEAR_RANGES.find((range) => range.label === filters.yearLabel) ?? null
      : null;
    const filtered = filterMovies({
      type,
      regions: filters.regions,
      yearRange,
      genres: filters.genres,
    });
    return sortMovies(filtered, sort);
  }, [type, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedResults = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggleRegion = (region: string) => {
    setFilters((prev) => ({
      ...prev,
      regions: prev.regions.includes(region)
        ? prev.regions.filter((r) => r !== region)
        : [...prev.regions, region],
    }));
    setPage(1);
  };

  const toggleGenre = (genre: string) => {
    setFilters((prev) => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter((g) => g !== genre)
        : [...prev.genres, genre],
    }));
    setPage(1);
  };

  const selectYear = (label: string | null) => {
    setFilters((prev) => ({ ...prev, yearLabel: label }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTER);
    setPage(1);
  };

  const activeCount = filters.regions.length + filters.genres.length + (filters.yearLabel ? 1 : 0);

  return (
    <Layout>
      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: typeInfo.title, to: filter ? `/category/${type}` : undefined },
            ...(filter ? [{ label: filter }] : []),
          ]}
        />

        <div className="mt-4">
          <h1 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
            {typeInfo.title}
            {filter ? ` · ${filter}` : ''}
          </h1>
          <p className="mt-2 text-sm text-foreground-500 max-w-2xl">{typeInfo.desc}</p>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-20 rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="text-sm font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-500"></i>
                筛选条件
              </h2>
              <FilterPanel
                state={filters}
                onToggleRegion={toggleRegion}
                onSelectYear={selectYear}
                onToggleGenre={toggleGenre}
                onReset={resetFilters}
              />
            </div>
          </aside>

          <div className="lg:col-span-9">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="lg:hidden h-9 px-3 flex items-center gap-1 rounded-md border border-background-200 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-filter-3-line"></i>
                  筛选
                  {activeCount > 0 && (
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500 text-background-950 text-[10px]">
                      {activeCount}
                    </span>
                  )}
                </button>
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  共 <strong className="text-foreground-900">{results.length}</strong> 部结果
                </span>
              </div>
              <div className="flex items-center gap-1 rounded-full border border-background-200 p-1">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSort(option.key);
                      setPage(1);
                    }}
                    className={`px-3 h-7 flex items-center gap-1 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      sort === option.key
                        ? 'bg-primary-500 text-background-950'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    <i className={option.icon}></i>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {pagedResults.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                  {pagedResults.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                <Pagination current={currentPage} total={totalPages} onChange={setPage} />
              </>
            ) : (
              <EmptyState
                icon="ri-emotion-sad-line"
                title="没有找到符合条件的内容"
                description="试试减少筛选条件，或重置后重新浏览全部作品。"
                actionLabel="重置筛选条件"
                onAction={resetFilters}
              />
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-background-950/70 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="relative w-full bg-background-50 rounded-t-2xl border-t border-background-200 max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-background-50 flex items-center justify-between px-5 py-4 border-b border-background-200">
              <h2 className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-500"></i>
                筛选条件
              </h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="px-5 py-5">
              <FilterPanel
                state={filters}
                onToggleRegion={toggleRegion}
                onSelectYear={selectYear}
                onToggleGenre={toggleGenre}
                onReset={resetFilters}
              />
            </div>
            <div className="sticky bottom-0 bg-background-50 px-5 py-4 border-t border-background-200">
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-full h-11 rounded-md bg-primary-500 text-background-950 text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                查看 {results.length} 部结果
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}